import * as React from "react";
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";

/**
 * Migrated from `@radix-ui/react-tabs` to Base UI.
 *
 * The public API of every export here is unchanged. 65 files consume this
 * component; the ones that change are the 12 that style through the state
 * attribute, which no wrapper can absorb because the class names the DOM
 * attribute directly.
 *
 * Absorbed here:
 *
 *  1. **`Trigger` → `Tab`** and **`Content` → `Panel`.** Base UI renamed both
 *     parts. The exports keep their names and their `data-slot`s, so callers
 *     and the `[&_[data-slot=…]]` selectors that reach into them are unaffected.
 *  2. **`forceMount` → `keepMounted`.** Radix's name for keeping a panel in the
 *     DOM while inactive, so its state and scroll position survive a tab
 *     switch. Nine call sites use it.
 *  3. **`onValueChange` gains a second argument.** Base UI passes `eventDetails`
 *     alongside the value; forwarding it would widen the callback every caller
 *     is typed against.
 *  4. **`asChild` → `render`.** 24 call sites make a router `<Link>` the tab, so
 *     that the tab bar is real navigation rather than a click handler.
 *     `resolveRender` translates, as it does for Collapsible and DropdownMenu.
 *  5. **A disabled tab is no longer natively disabled.** Base UI builds the tab
 *     with `focusableWhenDisabled`, so it gets `aria-disabled` and
 *     `data-disabled` and stays reachable by keyboard, where Radix set the
 *     native `disabled` attribute. Better behaviour, kept — but Tailwind's
 *     `disabled:` variant compiles to `&:disabled` and stopped matching, which
 *     the `DisabledTab` screenshot caught as a 107-pixel diff. Both spellings
 *     are carried now, the same fix Accordion's trigger needed (#6176).
 *
 * Not absorbable, because callers style through them — Radix's single
 * `data-state="active|inactive"` becomes two attributes that do **not** mirror
 * each other:
 *
 *  - the **tab** carries `data-active`, present only when active;
 *  - the **panel** carries `data-hidden`, present only when hidden.
 *
 * So `data-[state=active]:` on a trigger becomes `data-[active]:`, while on a
 * panel it becomes `not-data-[hidden]:` — and `data-[state=inactive]:` on a
 * panel becomes `data-[hidden]:`. All 26 lines across 12 files are rewritten in
 * the same commit.
 */
function Tabs({
  className,
  onValueChange,
  ...props
}: Omit<TabsPrimitive.Root.Props, "onValueChange" | "className"> & {
  onValueChange?: (value: string) => void;
  className?: string;
}) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-1", className)}
      onValueChange={(value) => onValueChange?.(String(value))}
      {...props}
    />
  );
}

function TabsList({
  className,
  ...props
}: Omit<TabsPrimitive.List.Props, "className"> & { className?: string }) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "text-muted-foreground inline-flex h-9 w-full items-center bg-background justify-start rounded-none border-b p-0",
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  asChild,
  render,
  children,
  ...props
}: Omit<TabsPrimitive.Tab.Props, "value" | "className"> & {
  value: string;
  className?: string;
  asChild?: boolean;
}) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      className={cn(
        "bg-background text-muted-foreground inline-flex h-full items-center justify-center gap-1.5 whitespace-nowrap rounded-none rounded-t border border-transparent px-2 py-1 text-sm font-medium transition-[color,box-shadow]",
        "data-[active]:-mb-0.5 data-[active]:border-border data-[active]:shadow-none data-[active]:border-b-background data-[active]:bg-background data-[active]:text-foreground",
        "dark:data-[active]:bg-background dark:data-[active]:-mb-0.5 dark:border-b-0 dark:data-[active]:border-input dark:text-muted-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:pointer-events-none disabled:opacity-50",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </TabsPrimitive.Tab>
  );
}

function TabsContent({
  className,
  forceMount,
  ...props
}: Omit<TabsPrimitive.Panel.Props, "value" | "className" | "keepMounted"> & {
  value: string;
  className?: string;
  /**
   * Radix's spelling of Base UI's `keepMounted`: keep the panel in the DOM
   * while inactive so its state and scroll position survive a tab switch. Nine
   * call sites use it, all of them hiding the inactive panel in CSS instead.
   *
   * `hidden={false}` goes with it, and is the whole reason this prop needs
   * handling rather than a rename. Radix **omits** the native `hidden`
   * attribute on a `forceMount` panel precisely so the caller's CSS can decide;
   * Base UI sets it on any closed panel regardless. Left alone, a caller using
   * `invisible` — which preserves layout — would silently get `display: none`
   * and a collapsed panel instead. `ARCustomerProfilePage` depends on the
   * difference, and `tabs.browser.test.tsx` asserts the inactive panel's height
   * because nothing about its *visibility* would fail either way.
   *
   * Base UI still sets `inert` on the closed panel, which Radix did not. That
   * is kept: an invisible panel should not be interactive, and it is a strictly
   * better answer than Radix's.
   */
  forceMount?: boolean;
}) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      {...(forceMount ? { keepMounted: true, hidden: false } : {})}
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
